import { doTrack } from '@/track/track.service';

export type TemplateSourceFlow = 'Tool' | 'Scaling Table';

export interface TemplateExecuteInfo {
  sourceFlow: TemplateSourceFlow;
  numInputs: number;
  numIntermediates: number;
  numOutputs: number;
  templateVersion?: number;
  templateName?: string;
  templateId?: string;
  isReapply?: boolean;
  scalingTableId?: string;
}

function buildExecuteTrackInfo(info: TemplateExecuteInfo): Record<string, string | number | boolean | undefined> {
  return {
    sourceFlow: info.sourceFlow,
    numInputs: info.numInputs,
    numIntermediates: info.numIntermediates,
    numOutputs: info.numOutputs,
    ...(info.templateVersion !== undefined && { templateVersion: info.templateVersion }),
    ...(info.templateName !== undefined && { templateName: info.templateName }),
    ...(info.templateId !== undefined && { templateId: info.templateId }),
    ...(info.isReapply !== undefined && { isReapply: info.isReapply }),
    ...(info.scalingTableId !== undefined && { scalingTableId: info.scalingTableId }),
  };
}

export function trackApplyExecute(info: TemplateExecuteInfo): void {
  doTrack('Template', 'Apply execute button clicked', buildExecuteTrackInfo(info));
}

export function trackEditExecute(info: TemplateExecuteInfo): void {
  doTrack('Template', 'Edit execute button clicked', buildExecuteTrackInfo(info));
}

export function trackPublish(info: TemplateExecuteInfo): void {
  doTrack('Template', 'Publish button clicked', buildExecuteTrackInfo(info));
}

export function trackSaveDraft(info: TemplateExecuteInfo): void {
  doTrack('Template', 'Save draft button clicked', buildExecuteTrackInfo(info));
}

export function trackCancelApply(sourceFlow: TemplateSourceFlow, templateId?: string): void {
  doTrack('Template', 'Cancel apply button clicked', { sourceFlow, ...(templateId && { templateId }) });
}

export function trackCancelCreate(sourceFlow: TemplateSourceFlow): void {
  doTrack('Template', 'Cancel create button clicked', { sourceFlow });
}

export function trackCancelEdit(sourceFlow: TemplateSourceFlow, templateId?: string): void {
  doTrack('Template', 'Cancel edit button clicked', { sourceFlow, ...(templateId && { templateId }) });
}

export function trackCancelLock(sourceFlow: TemplateSourceFlow, templateId: string): void {
  doTrack('Template', 'Cancel lock button clicked', { sourceFlow, templateId });
}

export function trackCancelCompatibility(sourceFlow: TemplateSourceFlow, templateId: string): void {
  doTrack('Template', 'Cancel compatibility button clicked', { sourceFlow, templateId });
}

export function trackEditOptionClicked(templateId: string, isDraft: boolean): void {
  doTrack('Template', 'Edit option clicked', { templateId, isDraft });
}

export function trackDuplicateOptionClicked(sourceTemplateId: string): void {
  doTrack('Template', 'Duplicate option clicked', { sourceTemplateId });
}

export function trackApplyOnceOptionClicked(templateId: string): void {
  doTrack('Template', 'Apply once option clicked', { templateId });
}

export function trackApplyToScalingTableOptionClicked(templateId?: string, sourceFlow?: TemplateSourceFlow): void {
  doTrack('Template', 'Apply to scaling table option clicked', {
    ...(templateId && { templateId }),
    ...(sourceFlow && { sourceFlow }),
  });
}

export function trackEditButtonClicked(sourceFlow: TemplateSourceFlow, templateId?: string): void {
  doTrack('Template', 'Edit button clicked', { sourceFlow, ...(templateId && { templateId }) });
}

export function trackCreateButtonClicked(fromSource: string, sourceFlow: TemplateSourceFlow): void {
  doTrack('Template', 'Create button clicked', { fromSource, sourceFlow });
}

export function trackTemplateLocked(templateId: string): void {
  doTrack('Template', 'Template locked', { templateId });
}

export function trackRetireOneOffInstance(templateId: string): void {
  doTrack('Template', 'Retire one off instance', { templateId });
}

export function trackRetireScalingTableInstance(
  templateId: string,
  scalingTableId: string,
  deleteColumnsChecked: boolean,
): void {
  doTrack('Template', 'Retire scaling table instance', { templateId, scalingTableId, deleteColumnsChecked });
}

export function trackEditApplication(sourceFlow: TemplateSourceFlow, templateId: string, templateName: string): void {
  doTrack('Template', 'Edit application', { sourceFlow, templateId, templateName });
}

export function trackDeleteButtonClicked(itemId: string, isHierarchy: boolean): void {
  const trackCategory = isHierarchy ? 'Tree Builder' : 'Template';
  const trackInformation = isHierarchy ? { treeId: itemId } : { templateId: itemId };
  doTrack(trackCategory, 'Delete button clicked', trackInformation);
}
